import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '@environments/environment';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private renderer: Renderer2;

  defaultImg = environment.urlIMG + 'logo-red.png';

  constructor(
    private title: Title,
    private meta: Meta,
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  // -------------------
  // Métodos base genéricos
  // -------------------
  updateTitle(title: string) {
    this.title.setTitle(title);
  }

  updateDescription(desc: string) {
    this.meta.updateTag({ name: 'description', content: desc });
  }

  updateOpenGraph(data: {
    title: string;
    description: string;
    url: string;
    image?: string;
  }) {
    this.meta.updateTag({ property: 'og:title', content: data.title });
    this.meta.updateTag({ name: 'twitter:title', content: data.title });

    this.meta.updateTag({
      property: 'og:description',
      content: data.description,
    });
    this.meta.updateTag({
      name: 'twitter:description',
      content: data.description,
    });

    this.meta.updateTag({ property: 'og:url', content: data.url });

    this.meta.updateTag({
      name: 'twitter:image',
      content: data.image ?? this.defaultImg,
    });

    this.meta.updateTag({
      property: 'og:image',
      content: data.image ?? this.defaultImg,
    });
  }

  setCanonical(url: string) {
    let link: HTMLLinkElement =
      this.document.querySelector("link[rel='canonical']") ||
      this.renderer.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', url);
    if (!link.parentNode) {
      this.renderer.appendChild(this.document.head, link);
    }
  }

  setJsonLd(schema: object) {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.renderer.appendChild(this.document.head, script);
  }

  // -------------------
  // Métodos especializados
  // -------------------

  /** ⚽ Partido */
  setMatchSeo(match: {
    homeTeam: string;
    awayTeam: string;
    date: string;
    location: string;
    url: string;
    image?: string;
  }) {
    const title = `${match.homeTeam} vs ${match.awayTeam} | Resultado en vivo | Marcadores247`;
    const description = `Sigue el resultado en vivo de ${match.homeTeam} vs ${match.awayTeam}, estadísticas y detalles del partido.`;

    this.updateTitle(title);
    this.updateDescription(description);
    this.updateOpenGraph({
      title,
      description,
      url: match.url,
      image: match.image,
    });
    this.setCanonical(match.url);

    // this.setJsonLd({
    //   '@context': 'https://schema.org',
    //   '@type': 'SportsEvent',
    //   name: `${match.homeTeam} vs ${match.awayTeam}`,
    //   sport: 'Soccer',
    //   startDate: match.date,
    //   location: { '@type': 'Place', name: match.location },
    //   homeTeam: { '@type': 'SportsTeam', name: match.homeTeam },
    //   awayTeam: { '@type': 'SportsTeam', name: match.awayTeam },
    // });

    this.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SportsEvent',
      name: `${match.homeTeam} vs ${match.awayTeam}`,
      description: `Partido en vivo entre ${match.homeTeam} y ${match.awayTeam} en ${match.location}. Sigue el marcador y estadísticas.`,
      sport: 'Soccer',
      startDate: match.date,
      endDate: new Date(
        new Date(match.date).getTime() + 2 * 60 * 60 * 1000
      ).toISOString(), // ejemplo +2h
      eventStatus: 'https://schema.org/EventScheduled',
      location: {
        '@type': 'Place',
        name: match.location,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Ciudad', // ⚠️ aquí deberías poner dinámico si lo tienes
          addressCountry: '', // ISO code del país
        },
      },
      homeTeam: {
        '@type': 'SportsTeam',
        name: match.homeTeam,
      },
      awayTeam: {
        '@type': 'SportsTeam',
        name: match.awayTeam,
      },
      offers: {
        '@type': 'Offer',
        url: match.url,
        price: '0', // si es gratis o placeholder
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        validFrom: match.date,
      },
    });
  }

  /** 🏟️ Equipo */
  setTeamSeo(team: {
    name: string;
    // league: string;
    url: string;
    image?: string;
  }) {
    const title = `${team.name} | Plantilla, resultados y estadísticas | Marcadores247`;
    const description = `Consulta la información del equipo ${team.name}, resultados, plantilla y estadísticas en su liga.`;
    // const description = `Consulta la información del equipo ${team.name}, resultados, plantilla y estadísticas en ${team.league}.`;

    this.updateTitle(title);
    this.updateDescription(description);
    this.updateOpenGraph({
      title,
      description,
      url: team.url,
      image: team.image,
    });
    this.setCanonical(team.url);

    this.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SportsTeam',
      name: team.name,
      sport: 'Soccer',
      // memberOf: { '@type': 'SportsOrganization', name: team.league },
    });
  }

  /** 🏆 Liga/Competición */
  setLeagueSeo(league: {
    name: string;
    // country: string;
    url: string;
    image?: string;
  }) {
    const title = `${league.name} | Resultados y clasificación | Marcadores247`;
    // const title = `${league.name} (${league.country}) | Resultados y clasificación | Marcadores247`;
    const description = `Sigue todos los partidos, resultados y clasificación de ${league.name}.`;
    // const description = `Sigue todos los partidos, resultados y clasificación de ${league.name} (${league.country}).`;

    this.updateTitle(title);
    this.updateDescription(description);
    this.updateOpenGraph({
      title,
      description,
      url: league.url,
      image: league.image,
    });
    this.setCanonical(league.url);

    this.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'SportsOrganization',
      name: league.name,
      sport: 'Soccer',
      // location: league.country,
    });
  }

  /** 📰 Noticias */
  setNewsSeo(news: {
    title: string;
    description: string;
    url: string;
    image?: string;
    date?: string;
    author?: string;
    keywords?: string;
  }) {
    this.updateTitle(news.title);
    this.updateDescription(news.description);
    this.updateOpenGraph({
      title: news.title,
      description: news.description,
      url: news.url,
      image: news.image,
    });
    this.setCanonical(news.url);

    this.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: news.title,
      image: news.image ? [news.image] : [],
      datePublished: news.date || new Date(),
      author: {
        '@type': 'Organization',
        name: news.author || 'Marcadores247',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Marcadores247',
        logo: {
          '@type': 'ImageObject',
          url: 'https://marcadores247.com/assets/logo.png',
        },
      },
      keywords: news.keywords || [],
    });

    this.meta.updateTag({
      name: 'keywords',
      content: news.keywords || '',
    });
  }
}
